import { useState } from 'react';
import type { QuestionAnswerInput } from '../api/client';
import type { Problem, QuestionItem, StudioQuestion } from '../api/types';
import { Button } from '../components/Button';
import { ProblemNotice } from '../components/ProblemNotice';
import { toProblem } from '../app/useAsync';

export interface QuestionCardProps {
  question: StudioQuestion;
  /** 失敗したら投げる。カードが理由を出し、もう一度押せるようにする。 */
  onSubmit(answers: QuestionAnswerInput[]): Promise<void>;
}

interface AnswerDraft {
  choices: string[];
  note: string;
  defer: boolean;
}

const EMPTY: AnswerDraft = { choices: [], note: '', defer: false };

const KIND_LABELS: Record<QuestionItem['kind'], string> = {
  single: '単一選択',
  multi: '複数選択',
  text: '自由記述',
};

/**
 * AI からの質問（ask_user / request_knowledge_source。C44）。問いごとに選択肢・補足・「あとで決める」を持ち、
 * すべての問いがそろったら 1 回で送る。choices は選択肢の id（C19）。
 */
export function QuestionCard({ question, onSubmit }: QuestionCardProps) {
  const items = question.body.questions;
  const [answers, setAnswers] = useState<Record<number, AnswerDraft>>({});
  const [submitting, setSubmitting] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);

  const update = (n: number, patch: Partial<AnswerDraft>) =>
    setAnswers((current) => ({ ...current, [n]: { ...(current[n] ?? EMPTY), ...patch } }));
  const complete = items.every((item) => isComplete(item, answers[item.n] ?? EMPTY));

  const submit = async () => {
    if (!complete || submitting) return;
    setSubmitting(true);
    setProblem(null);
    try {
      await onSubmit(items.map((item) => toAnswer(item, answers[item.n] ?? EMPTY)));
    } catch (error) {
      setProblem(toProblem(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      className="studio-question studio-question-attention"
      aria-label="AI からの質問"
      // 回答待ちの帯から scrollIntoView で探す目印（U6）。
      data-question-id={question.id}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <p className="studio-question-waiting">AI が待っています</p>
      {items.map((item) => {
        const answer = answers[item.n] ?? EMPTY;
        return (
          <fieldset key={item.n} className="studio-question-item" disabled={submitting}>
            <legend>{`質問 ${item.n} / ${items.length} · ${KIND_LABELS[item.kind]}`}</legend>
            <p className="studio-question-text">{item.text}</p>
            {item.kind !== 'text' &&
              item.options.map((option) => (
                <label key={option.id} className="studio-question-option">
                  <input
                    type={item.kind === 'single' ? 'radio' : 'checkbox'}
                    name={`${question.id}-${item.n}`}
                    value={option.id}
                    checked={answer.choices.includes(option.id)}
                    disabled={answer.defer}
                    onChange={(event) =>
                      update(item.n, {
                        choices:
                          item.kind === 'single'
                            ? [option.id]
                            : event.target.checked
                              ? [...answer.choices, option.id]
                              : answer.choices.filter((choice) => choice !== option.id),
                      })
                    }
                  />
                  <span>{option.label}</span>
                  {option.recommended && <span className="studio-question-recommended">推奨</span>}
                  {option.evidence && <span className="studio-question-evidence">{`根拠：${option.evidence}`}</span>}
                </label>
              ))}
            <textarea
              className="field-input studio-question-note"
              aria-label={item.kind === 'text' ? '回答' : '補足'}
              placeholder={item.kind === 'text' ? '回答を書いてください' : '補足があれば書いてください（任意）'}
              value={answer.note}
              disabled={answer.defer}
              rows={2}
              onChange={(event) => update(item.n, { note: event.target.value })}
            />
            {item.allowDefer && (
              <label className="studio-question-defer">
                <input type="checkbox" checked={answer.defer} onChange={(event) => update(item.n, { defer: event.target.checked })} />
                あとで決める
              </label>
            )}
          </fieldset>
        );
      })}
      <ProblemNotice problem={problem} />
      <div className="row">
        <span className="spacer" />
        <Button type="submit" variant="primary" disabled={!complete || submitting}>
          {submitting ? '送信中…' : '回答する'}
        </Button>
      </div>
    </form>
  );
}

function isComplete(item: QuestionItem, answer: AnswerDraft): boolean {
  if (answer.defer) return true;
  if (item.kind === 'text') return answer.note.trim() !== '';
  if (item.kind === 'single') return answer.choices.length === 1;
  return answer.choices.length > 0;
}

/** API の parse_answers と同じ形。defer の問いは defer だけ、補足は空なら送らない。choices は選択肢の並び順。 */
function toAnswer(item: QuestionItem, answer: AnswerDraft): QuestionAnswerInput {
  if (answer.defer) return { n: item.n, defer: true };
  const note = answer.note.trim();
  if (item.kind === 'text') return { n: item.n, text: note };
  const choices = item.options.map((option) => option.id).filter((id) => answer.choices.includes(id));
  return note ? { n: item.n, choices, text: note } : { n: item.n, choices };
}
